import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import PinDropIcon from "@mui/icons-material/PinDrop";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RouteIcon from "@mui/icons-material/Route";
import { API_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const TRANSPORT_TABS = [
  { key: "buses", label: "Bus Details", icon: <DirectionsBusIcon /> },
  { key: "stops", label: "Bus Stops", icon: <PinDropIcon /> },
  { key: "routes", label: "Route Mapping", icon: <AltRouteIcon /> },
];

const emptyBus = (CampusCode = "") => ({
  CampusCode,
  BusNumber: "",
  DriverName: "",
  Capacity: "",
  FuelType: "",
  LicensePlate: "",
  ManufactureYear: "",
  Status: "Active",
});

const emptyStop = (CampusCode = "") => ({
  CampusCode,
  StopName: "",
  Distance: "",
  TransportFee: "",
  IsActive: 1,
});

const normalizeTime = (value) => {
  if (!value) return "";
  const s = String(value);
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  if (/^\d{2}:\d{2}:\d{2}/.test(s)) return s.slice(0, 5);
  return s;
};

export default function SetupTransport() {
  // Exact theme family used by SetupAcademicCode.
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const BLUE = "#1565C0";

  const token = localStorage.getItem("auth") || localStorage.getItem("token");
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const [tab, setTab] = useState(0);
  const activeTab = TRANSPORT_TABS[tab];
  const [campusCode, setCampusCode] = useState("");
  const [msg, setMsg] = useState({ type: "success", text: "" });
  const [loading, setLoading] = useState(false);

  // Buses
  const [buses, setBuses] = useState([]);
  const [activeBuses, setActiveBuses] = useState([]);
  const [busDialogOpen, setBusDialogOpen] = useState(false);
  const [busMode, setBusMode] = useState("add");
  const [busDraft, setBusDraft] = useState(emptyBus(""));
  const [busPage, setBusPage] = useState(0);
  const [busRowsPerPage, setBusRowsPerPage] = useState(25);
  const busFileRef = useRef(null);

  // Stops
  const [stops, setStops] = useState([]);
  const [activeStops, setActiveStops] = useState([]);
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [stopMode, setStopMode] = useState("add");
  const [stopDraft, setStopDraft] = useState(emptyStop(""));
  const [stopPage, setStopPage] = useState(0);
  const [stopRowsPerPage, setStopRowsPerPage] = useState(25);
  const stopFileRef = useRef(null);

  // Route mappings
  const [mappings, setMappings] = useState([]);
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [routeMode, setRouteMode] = useState("add");
  const [routeBusId, setRouteBusId] = useState("");
  const [routeStops, setRouteStops] = useState([]);
  const [routePage, setRoutePage] = useState(0);
  const [routeRowsPerPage, setRouteRowsPerPage] = useState(25);
  const [routeSaving, setRouteSaving] = useState(false);

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
  };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg,${THEME_ORANGE},${THEME_ORANGE_2})`,
    },
  };
  const popupSectionSx = {
    ...surfaceSx,
    p: 2.5,
    borderRadius: 2.5,
    borderTop: `4px solid ${THEME_ORANGE}`,
    overflow: "hidden",
  };
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: BORDER_HOVER },
      "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };
  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 800,
    borderColor: "rgba(25,118,210,0.35)",
    color: BLUE,
    backgroundColor: "white",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };
  const iconBtnSx = {
    color: BLUE,
    "&:hover": {
      backgroundColor: "rgba(240,120,40,0.10)",
      color: THEME_ORANGE,
    },
  };
  const dangerIconBtnSx = {
    color: "rgba(244,67,54,0.90)",
    "&:hover": {
      backgroundColor: "rgba(244,67,54,0.08)",
      color: "rgba(244,67,54,1)",
    },
  };
  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      whiteSpace: "nowrap",
      borderBottom: "1px solid rgba(255,255,255,0.18)",
    },
  };
  const zebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? ROW_ALT : "#fff",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });
  const tabsSx = {
    px: 1,
    borderBottom: `1px solid ${BORDER}`,
    minHeight: 48,
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 800,
      minHeight: 48,
    },
    "& .Mui-selected": { color: `${THEME_ORANGE} !important` },
    "& .MuiTabs-indicator": {
      backgroundColor: THEME_ORANGE,
      height: 3,
    },
  };
  const checkboxSx = {
    color: BLUE,
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  const serialBadge = (number) => (
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 900,
        fontSize: "1rem",
        color: "#1976D2",
        backgroundColor: "rgba(25,118,210,0.06)",
        border: "2px solid rgba(25,118,210,0.20)",
        lineHeight: 1,
      }}
    >
      {number}
    </Box>
  );

  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      const cc = decoded.campusCode || decoded.CampusCode || "";
      setCampusCode(cc);
      setBusDraft(emptyBus(cc));
      setStopDraft(emptyStop(cc));
    } catch {
      setMsg({ type: "error", text: "Failed to decode authentication token." });
    }
  }, [token]);

  const fetchAll = async ({ silent = false } = {}) => {
    if (!campusCode) return;
    if (!silent) setLoading(true);
    try {
      const [allBusRes, activeBusRes, allStopRes, activeStopRes, mapRes] =
        await Promise.all([
          axios.get(`${API_URL}/api/Getbuses`, { headers: authHeaders }),
          axios.get(`${API_URL}/api/buses`, { headers: authHeaders }),
          axios.get(`${API_URL}/api/allBusStops`, { headers: authHeaders }),
          axios.get(`${API_URL}/api/busStops`, { headers: authHeaders }),
          axios.get(`${API_URL}/api/busStopMappings`, { headers: authHeaders }),
        ]);

      setBuses(Array.isArray(allBusRes.data) ? allBusRes.data : []);
      setActiveBuses(Array.isArray(activeBusRes.data) ? activeBusRes.data : []);
      setStops(Array.isArray(allStopRes.data) ? allStopRes.data : []);
      setActiveStops(Array.isArray(activeStopRes.data) ? activeStopRes.data : []);
      setMappings(Array.isArray(mapRes.data) ? mapRes.data : []);
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || "Failed to load transport setup data.",
      });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode]);

  // ------------------------------------------------------------
  // BUS DETAILS
  // ------------------------------------------------------------
  const downloadBusSample = () => {
    const cc = campusCode || "CAMPUS01";
    const rows = [
      {
        CampusCode: cc,
        BusNumber: "BUS-01",
        DriverName: "Ramesh Kumar",
        Capacity: 50,
        FuelType: "Diesel",
        LicensePlate: "AP39AB1234",
        ManufactureYear: 2018,
        Status: "Active",
      },
      {
        CampusCode: cc,
        BusNumber: "BUS-02",
        DriverName: "Suresh Naidu",
        Capacity: 40,
        FuelType: "CNG",
        LicensePlate: "AP39CD5678",
        ManufactureYear: 2020,
        Status: "Under Maintenance",
      },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0]).map((h) => ({
      wch: Math.max(16, h.length + 2),
    }));
    const help = XLSX.utils.aoa_to_sheet([
      ["Field", "Allowed / Notes"],
      ["CampusCode", "Use the logged-in campus code"],
      ["FuelType", "CNG / Diesel / Electric / Hybrid / Petrol"],
      ["Status", "Active / Under Maintenance / Out of Service"],
      ["Capacity", "Numeric seat capacity"],
      ["ManufactureYear", "4-digit year"],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BusDetails");
    XLSX.utils.book_append_sheet(wb, help, "Instructions");
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([out], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Transport_Buses_Sample_${cc}.xlsx`
    );
  };

  const uploadBuses = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", campusCode);
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/UploadBusDetails`, form, {
        headers: {
          ...authHeaders,
          "Content-Type": "multipart/form-data",
        },
      });
      setMsg({ type: "success", text: "Bus details bulk upload completed." });
      await fetchAll({ silent: true });
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || "Bus details bulk upload failed.",
      });
    } finally {
      setLoading(false);
    }
  };

  const openAddBus = () => {
    setBusMode("add");
    setBusDraft(emptyBus(campusCode));
    setBusDialogOpen(true);
  };

  const openEditBus = (row) => {
    setBusMode("edit");
    setBusDraft({ ...emptyBus(campusCode), ...row });
    setBusDialogOpen(true);
  };

  const saveBus = async () => {
    if (!String(busDraft.BusNumber || "").trim()) {
      return setMsg({ type: "error", text: "Bus Number is required." });
    }
    try {
      if (busMode === "add") {
        await axios.post(
          `${API_URL}/api/buses`,
          { ...busDraft, CampusCode: campusCode },
          { headers: authHeaders }
        );
      } else {
        await axios.put(
          `${API_URL}/api/buses/${busDraft.BusID}`,
          busDraft,
          { headers: authHeaders }
        );
      }
      setMsg({
        type: "success",
        text: busMode === "add" ? "Bus added successfully." : "Bus updated successfully.",
      });
      setBusDialogOpen(false);
      await fetchAll({ silent: true });
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || "Failed to save bus.",
      });
    }
  };

  const deleteBus = async (row) => {
    if (!window.confirm(`Delete bus "${row.BusNumber}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/buses/${row.BusID}`, {
        headers: authHeaders,
      });
      setMsg({ type: "success", text: "Bus deleted successfully." });
      await fetchAll({ silent: true });
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || "Failed to delete bus.",
      });
    }
  };

  // ------------------------------------------------------------
  // BUS STOPS
  // ------------------------------------------------------------
  const downloadStopSample = () => {
    const cc = campusCode || "CAMPUS01";
    const rows = [
      {
        CampusCode: cc,
        StopName: "Main Gate",
        Distance: 3.5,
        TransportFee: 1200,
        IsActive: 1,
      },
      {
        CampusCode: cc,
        StopName: "Railway Station",
        Distance: 8,
        TransportFee: 2500,
        IsActive: 1,
      },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0]).map((h) => ({
      wch: Math.max(14, h.length + 2),
    }));
    const help = XLSX.utils.aoa_to_sheet([
      ["Field", "Notes / Example"],
      ["CampusCode", "Use logged-in campus code"],
      ["StopName", "Example: Main Gate"],
      ["Distance", "Distance in KM, e.g. 3.5"],
      ["TransportFee", "Numeric fee, e.g. 1200"],
      ["IsActive", "1 = Active, 0 = Inactive"],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BusStops");
    XLSX.utils.book_append_sheet(wb, help, "Instructions");
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([out], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `BusStops_Sample_${cc}.xlsx`
    );
  };

  const uploadStops = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", campusCode);
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/UploadBusStops`, form, {
        headers: {
          ...authHeaders,
          "Content-Type": "multipart/form-data",
        },
      });
      setMsg({ type: "success", text: "Bus stop bulk upload completed." });
      // Correct refresh: reload bus stops, not CodeSubCampus.
      await fetchAll({ silent: true });
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || "Bus stop bulk upload failed.",
      });
    } finally {
      setLoading(false);
    }
  };

  const openAddStop = () => {
    setStopMode("add");
    setStopDraft(emptyStop(campusCode));
    setStopDialogOpen(true);
  };

  const openEditStop = (row) => {
    setStopMode("edit");
    setStopDraft({ ...emptyStop(campusCode), ...row });
    setStopDialogOpen(true);
  };

  const saveStop = async () => {
    if (!String(stopDraft.StopName || "").trim()) {
      return setMsg({ type: "error", text: "Stop Name is required." });
    }
    try {
      if (stopMode === "add") {
        await axios.post(
          `${API_URL}/api/busStops`,
          { ...stopDraft, CampusCode: campusCode },
          { headers: authHeaders }
        );
      } else {
        await axios.put(
          `${API_URL}/api/busStops/${stopDraft.StopID}`,
          stopDraft,
          { headers: authHeaders }
        );
      }
      setMsg({
        type: "success",
        text:
          stopMode === "add"
            ? "Bus stop added successfully."
            : "Bus stop updated successfully.",
      });
      setStopDialogOpen(false);
      await fetchAll({ silent: true });
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || "Failed to save bus stop.",
      });
    }
  };

  const deleteStop = async (row) => {
    if (!window.confirm(`Delete stop "${row.StopName}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/busStops/${row.StopID}`, {
        headers: authHeaders,
      });
      setMsg({ type: "success", text: "Bus stop deleted successfully." });
      await fetchAll({ silent: true });
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || "Failed to delete bus stop.",
      });
    }
  };

  // ------------------------------------------------------------
  // ROUTE BUILDER
  // ------------------------------------------------------------
  const routeGroups = useMemo(() => {
    const map = new Map();
    for (const m of mappings) {
      const id = String(m.BusID);
      if (!map.has(id)) {
        const busMeta =
          activeBuses.find((b) => String(b.BusID) === String(m.BusID)) ||
          buses.find((b) => String(b.BusID) === String(m.BusID));

        map.set(id, {
          BusID: m.BusID,
          BusNumber: m.BusNumber,
          DriverName: busMeta?.DriverName || "",
          stops: [],
        });
      }
      map.get(id).stops.push({
        BusStopMappingID: m.BusStopMappingID,
        StopID: m.StopID,
        StopName: m.StopName,
        StopOrder: Number(m.StopOrder || 0),
        StopTime: normalizeTime(m.StopTime),
      });
    }
    for (const route of map.values()) {
      route.stops.sort((a, b) => a.StopOrder - b.StopOrder);
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.BusNumber).localeCompare(String(b.BusNumber))
    );
  }, [mappings, activeBuses, buses]);

  const selectedBus = useMemo(
    () =>
      activeBuses.find((b) => String(b.BusID) === String(routeBusId)) ||
      buses.find((b) => String(b.BusID) === String(routeBusId)),
    [activeBuses, buses, routeBusId]
  );

  const currentBusMappings = useMemo(
    () =>
      mappings
        .filter((m) => String(m.BusID) === String(routeBusId))
        .sort((a, b) => Number(a.StopOrder) - Number(b.StopOrder)),
    [mappings, routeBusId]
  );

  const reindexRoute = (items) =>
    items.map((item, idx) => ({ ...item, StopOrder: idx + 1 }));

  const openAddRoute = () => {
    setRouteMode("add");
    setRouteBusId("");
    setRouteStops([]);
    setRouteDialogOpen(true);
  };

  const openEditRoute = (route) => {
    setRouteMode("edit");
    setRouteBusId(route.BusID);
    setRouteStops(
      reindexRoute(
        route.stops.map((s) => ({
          BusStopMappingID: s.BusStopMappingID,
          StopID: s.StopID,
          StopName: s.StopName,
          StopTime: normalizeTime(s.StopTime),
        }))
      )
    );
    setRouteDialogOpen(true);
  };

  const loadSelectedBusRoute = (busId) => {
    setRouteBusId(busId || "");
    const existing = mappings
      .filter((m) => String(m.BusID) === String(busId))
      .sort((a, b) => Number(a.StopOrder) - Number(b.StopOrder))
      .map((m) => ({
        BusStopMappingID: m.BusStopMappingID,
        StopID: m.StopID,
        StopName: m.StopName,
        StopTime: normalizeTime(m.StopTime),
      }));
    setRouteStops(reindexRoute(existing));
  };

  const availableStopsForIndex = (index) => {
    const selectedElsewhere = new Set(
      routeStops
        .filter((_, i) => i !== index)
        .map((s) => String(s.StopID))
        .filter(Boolean)
    );
    return activeStops.filter(
      (s) => !selectedElsewhere.has(String(s.StopID))
    );
  };

  const insertRouteStop = (index) => {
    const next = [...routeStops];
    next.splice(index, 0, {
      BusStopMappingID: null,
      StopID: "",
      StopName: "",
      StopTime: "",
      StopOrder: index + 1,
    });
    setRouteStops(reindexRoute(next));
  };

  const removeRouteStop = (index) => {
    setRouteStops(reindexRoute(routeStops.filter((_, i) => i !== index)));
  };

  const moveRouteStop = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= routeStops.length) return;
    const next = [...routeStops];
    [next[index], next[target]] = [next[target], next[index]];
    setRouteStops(reindexRoute(next));
  };

  const updateRouteStop = (index, patch) => {
    setRouteStops((prev) =>
      prev.map((item, i) => (i === index ? { ...item, ...patch } : item))
    );
  };

  const saveRoute = async () => {
    if (!routeBusId) {
      return setMsg({ type: "error", text: "Select a bus." });
    }
    if (!routeStops.length) {
      return setMsg({
        type: "error",
        text: "Add at least one stop to the route.",
      });
    }
    const incomplete = routeStops.some((s) => !s.StopID);
    if (incomplete) {
      return setMsg({
        type: "error",
        text: "Select a bus stop for every route position.",
      });
    }
    const ids = routeStops.map((s) => String(s.StopID));
    if (new Set(ids).size !== ids.length) {
      return setMsg({
        type: "error",
        text: "The same bus stop cannot appear twice in one route.",
      });
    }

    setRouteSaving(true);
    try {
      // New route-level API. It replaces/reorders the full route atomically.
      await axios.put(
        `${API_URL}/api/busStopMappings/route/${routeBusId}`,
        {
          stops: reindexRoute(routeStops).map((s) => ({
            BusStopMappingID: s.BusStopMappingID || undefined,
            StopID: Number(s.StopID),
            StopOrder: Number(s.StopOrder),
            StopTime: s.StopTime || null,
          })),
        },
        { headers: authHeaders }
      );

      setMsg({
        type: "success",
        text: "Bus route saved successfully.",
      });
      setRouteDialogOpen(false);
      await fetchAll({ silent: true });
    } catch (e) {
      setMsg({
        type: "error",
        text:
          e.response?.data?.error ||
          "Failed to save route. Make sure the updated route API is deployed.",
      });
    } finally {
      setRouteSaving(false);
    }
  };

  const deleteRoute = async (route) => {
    if (!window.confirm(`Delete the entire route for "${route.BusNumber}"?`)) {
      return;
    }
    try {
      await Promise.all(
        route.stops.map((s) =>
          axios.delete(
            `${API_URL}/api/busStopMappings/${s.BusStopMappingID}`,
            { headers: authHeaders }
          )
        )
      );
      setMsg({ type: "success", text: "Bus route deleted successfully." });
      await fetchAll({ silent: true });
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || "Failed to delete route.",
      });
    }
  };

  // Pagination
  const paginate = (rows, page, rowsPerPage) => {
    const maxPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    const safePage = Math.min(page, maxPage);
    return {
      safePage,
      maxPage,
      rows: rows.slice(
        safePage * rowsPerPage,
        safePage * rowsPerPage + rowsPerPage
      ),
    };
  };

  const busPg = paginate(buses, busPage, busRowsPerPage);
  const stopPg = paginate(stops, stopPage, stopRowsPerPage);
  const routePg = paginate(routeGroups, routePage, routeRowsPerPage);

  useEffect(() => {
    if (busPage > busPg.maxPage) setBusPage(busPg.maxPage);
  }, [busPage, busPg.maxPage]);
  useEffect(() => {
    if (stopPage > stopPg.maxPage) setStopPage(stopPg.maxPage);
  }, [stopPage, stopPg.maxPage]);
  useEffect(() => {
    if (routePage > routePg.maxPage) setRoutePage(routePg.maxPage);
  }, [routePage, routePg.maxPage]);

  // ------------------------------------------------------------
  // TABLES
  // ------------------------------------------------------------
  const renderBusTable = () => (
    <Stack spacing={2}>
      <Paper elevation={0} sx={sectionSx}>
        <Typography sx={{ fontWeight: 900, color: BLUE }}>Bus Details</Typography>
        <Typography sx={{ mt: 0.25, fontSize: "0.85rem", color: "text.secondary" }}>
          Maintain buses, drivers, vehicle details, capacity and operational status.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 1120 }}>
              <TableHead>
                <TableRow sx={theadRowSx}>
                  {[
                    "#",
                    "Bus Number",
                    "Driver",
                    "Capacity",
                    "Fuel Type",
                    "License Plate",
                    "Year",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <TableCell key={h} align={h === "Actions" ? "center" : "left"}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {busPg.rows.map((row, index) => {
                  const serial = busPg.safePage * busRowsPerPage + index + 1;
                  return (
                    <TableRow key={row.BusID} sx={zebraRowSx(index)}>
                      <TableCell>{serialBadge(serial)}</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>{row.BusNumber}</TableCell>
                      <TableCell>{row.DriverName || "—"}</TableCell>
                      <TableCell>{row.Capacity || "—"}</TableCell>
                      <TableCell>{row.FuelType || "—"}</TableCell>
                      <TableCell>{row.LicensePlate || "—"}</TableCell>
                      <TableCell>{row.ManufactureYear || "—"}</TableCell>
                      <TableCell>{row.Status || "—"}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <IconButton size="small" sx={iconBtnSx} onClick={() => openEditBus(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={dangerIconBtnSx} onClick={() => deleteBus(row)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!loading && !busPg.rows.length && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                        No buses found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell colSpan={9} sx={{ p: 0, borderBottom: 0 }}>
                    <Button
                      fullWidth
                      startIcon={<AddIcon />}
                      onClick={openAddBus}
                      sx={{
                        justifyContent: "center",
                        py: 1.35,
                        borderRadius: 0,
                        textTransform: "none",
                        fontWeight: 900,
                        color: THEME_ORANGE,
                        borderTop: `1px solid ${BORDER}`,
                        backgroundColor: "rgba(240,120,40,.035)",
                        "&:hover": { backgroundColor: "rgba(240,120,40,.085)" },
                      }}
                    >
                      Add Bus
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <TablePagination
          component="div"
          count={buses.length}
          page={busPg.safePage}
          onPageChange={(_, p) => setBusPage(p)}
          rowsPerPage={busRowsPerPage}
          onRowsPerPageChange={(e) => {
            setBusRowsPerPage(parseInt(e.target.value, 10));
            setBusPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100, 200, 300]}
          showFirstButton
          showLastButton
        />
      </Paper>
    </Stack>
  );

  const renderStopTable = () => (
    <Stack spacing={2}>
      <Paper elevation={0} sx={sectionSx}>
        <Typography sx={{ fontWeight: 900, color: BLUE }}>Bus Stops</Typography>
        <Typography sx={{ mt: 0.25, fontSize: "0.85rem", color: "text.secondary" }}>
          Maintain stop names, distance, transport fee and active status.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 850 }}>
              <TableHead>
                <TableRow sx={theadRowSx}>
                  {["#", "Stop Name", "Distance (KM)", "Transport Fee", "Active", "Actions"].map(
                    (h) => (
                      <TableCell key={h} align={h === "Actions" ? "center" : "left"}>
                        {h}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {stopPg.rows.map((row, index) => {
                  const serial = stopPg.safePage * stopRowsPerPage + index + 1;
                  return (
                    <TableRow key={row.StopID} sx={zebraRowSx(index)}>
                      <TableCell>{serialBadge(serial)}</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>{row.StopName}</TableCell>
                      <TableCell>{row.Distance ?? "—"}</TableCell>
                      <TableCell>{row.TransportFee ?? "—"}</TableCell>
                      <TableCell>
                        <Checkbox
                          checked={Number(row.IsActive) === 1}
                          disabled
                          sx={{
                            p: 0.5,
                            color: BLUE,
                            "&.Mui-checked": { color: THEME_ORANGE },
                            "&.Mui-disabled": {
                              color:
                                Number(row.IsActive) === 1
                                  ? `${THEME_ORANGE} !important`
                                  : "rgba(25,118,210,0.35) !important",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <IconButton size="small" sx={iconBtnSx} onClick={() => openEditStop(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={dangerIconBtnSx} onClick={() => deleteStop(row)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!loading && !stopPg.rows.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                        No bus stops found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, borderBottom: 0 }}>
                    <Button
                      fullWidth
                      startIcon={<AddIcon />}
                      onClick={openAddStop}
                      sx={{
                        justifyContent: "center",
                        py: 1.35,
                        borderRadius: 0,
                        textTransform: "none",
                        fontWeight: 900,
                        color: THEME_ORANGE,
                        borderTop: `1px solid ${BORDER}`,
                        backgroundColor: "rgba(240,120,40,.035)",
                        "&:hover": { backgroundColor: "rgba(240,120,40,.085)" },
                      }}
                    >
                      Add Bus Stop
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <TablePagination
          component="div"
          count={stops.length}
          page={stopPg.safePage}
          onPageChange={(_, p) => setStopPage(p)}
          rowsPerPage={stopRowsPerPage}
          onRowsPerPageChange={(e) => {
            setStopRowsPerPage(parseInt(e.target.value, 10));
            setStopPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100, 200, 300]}
          showFirstButton
          showLastButton
        />
      </Paper>
    </Stack>
  );

  const renderRouteTable = () => (
    <Stack spacing={2}>
      <Paper elevation={0} sx={sectionSx}>
        <Typography sx={{ fontWeight: 900, color: BLUE }}>Route Mapping</Typography>
        <Typography sx={{ mt: 0.25, fontSize: "0.85rem", color: "text.secondary" }}>
          Build each bus route, arrange stops in order and maintain stop timings.
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell>#</TableCell>
                  <TableCell>Bus</TableCell>
                  <TableCell>Driver Name</TableCell>
                  <TableCell>Stops</TableCell>
                  <TableCell>First Stop</TableCell>
                  <TableCell>Last Stop</TableCell>
                  <TableCell>Timing</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {routePg.rows.map((route, index) => {
                  const serial = routePg.safePage * routeRowsPerPage + index + 1;
                  const first = route.stops[0];
                  const last = route.stops[route.stops.length - 1];
                  return (
                    <TableRow key={route.BusID} sx={zebraRowSx(index)}>
                      <TableCell>{serialBadge(serial)}</TableCell>
                      <TableCell sx={{ fontWeight: 900, color: BLUE }}>{route.BusNumber}</TableCell>
                      <TableCell>{route.DriverName || "—"}</TableCell>
                      <TableCell>{route.stops.length}</TableCell>
                      <TableCell>{first?.StopName || "—"}</TableCell>
                      <TableCell>{last?.StopName || "—"}</TableCell>
                      <TableCell>
                        {first?.StopTime || "—"} {last?.StopTime ? `→ ${last.StopTime}` : ""}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <IconButton size="small" sx={iconBtnSx} onClick={() => openEditRoute(route)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={dangerIconBtnSx} onClick={() => deleteRoute(route)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!routePg.rows.length && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                        No bus routes configured.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell colSpan={8} sx={{ p: 0, borderBottom: 0 }}>
                    <Button
                      fullWidth
                      startIcon={<AddIcon />}
                      onClick={openAddRoute}
                      sx={{
                        justifyContent: "center",
                        py: 1.35,
                        borderRadius: 0,
                        textTransform: "none",
                        fontWeight: 900,
                        color: THEME_ORANGE,
                        borderTop: `1px solid ${BORDER}`,
                        backgroundColor: "rgba(240,120,40,.035)",
                        "&:hover": { backgroundColor: "rgba(240,120,40,.085)" },
                      }}
                    >
                      Add Bus Route
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <TablePagination
          component="div"
          count={routeGroups.length}
          page={routePg.safePage}
          onPageChange={(_, p) => setRoutePage(p)}
          rowsPerPage={routeRowsPerPage}
          onRowsPerPageChange={(e) => {
            setRouteRowsPerPage(parseInt(e.target.value, 10));
            setRoutePage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100, 200, 300]}
          showFirstButton
          showLastButton
        />
      </Paper>
    </Stack>
  );

  // ------------------------------------------------------------
  // DIALOGS
  // ------------------------------------------------------------
  const busDialog = (
    <Dialog
      open={busDialogOpen}
      onClose={() => setBusDialogOpen(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${BORDER}`,
          boxShadow: "0 18px 55px rgba(15,23,42,.22)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ ...headerSx, px: 3, py: 2 }}>
          <Typography sx={{ fontWeight: 900 }}>
            {busMode === "add" ? "Add Bus" : "Edit Bus"}
          </Typography>
          <Typography sx={{ fontSize: ".82rem", opacity: .9 }}>
            Maintain vehicle, driver and operational details.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: BG, p: { xs: 2, sm: 3 } }}>
        <Paper elevation={0} sx={{ ...popupSectionSx, mt: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(180px,1fr))" },
              gap: 2,
            }}
          >
            <TextField label="Campus" value={campusCode} disabled size="small" sx={inputSx} />
            <TextField
              label="Bus Number"
              value={busDraft.BusNumber || ""}
              onChange={(e) => setBusDraft((p) => ({ ...p, BusNumber: e.target.value }))}
              size="small"
              sx={inputSx}
            />
            <TextField
              label="Driver Name"
              value={busDraft.DriverName || ""}
              onChange={(e) => setBusDraft((p) => ({ ...p, DriverName: e.target.value }))}
              size="small"
              sx={inputSx}
            />
            <TextField
              label="Capacity"
              type="number"
              value={busDraft.Capacity || ""}
              onChange={(e) => setBusDraft((p) => ({ ...p, Capacity: e.target.value }))}
              size="small"
              sx={inputSx}
            />
            <TextField
              select
              label="Fuel Type"
              value={busDraft.FuelType || ""}
              onChange={(e) => setBusDraft((p) => ({ ...p, FuelType: e.target.value }))}
              size="small"
              sx={inputSx}
            >
              {["CNG", "Diesel", "Electric", "Hybrid", "Petrol"].map((x) => (
                <MenuItem key={x} value={x}>{x}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="License Plate"
              value={busDraft.LicensePlate || ""}
              onChange={(e) => setBusDraft((p) => ({ ...p, LicensePlate: e.target.value }))}
              size="small"
              sx={inputSx}
            />
            <TextField
              label="Manufacture Year"
              type="number"
              value={busDraft.ManufactureYear || ""}
              onChange={(e) => setBusDraft((p) => ({ ...p, ManufactureYear: e.target.value }))}
              size="small"
              sx={inputSx}
            />
            <TextField
              select
              label="Status"
              value={busDraft.Status || ""}
              onChange={(e) => setBusDraft((p) => ({ ...p, Status: e.target.value }))}
              size="small"
              sx={inputSx}
            >
              {["Active", "Under Maintenance", "Out of Service"].map((x) => (
                <MenuItem key={x} value={x}>{x}</MenuItem>
              ))}
            </TextField>
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${BORDER}` }}>
        <Button onClick={() => setBusDialogOpen(false)}>Cancel</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={saveBus} sx={primaryBtnSx}>
          {busMode === "add" ? "Add Bus" : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const stopDialog = (
    <Dialog
      open={stopDialogOpen}
      onClose={() => setStopDialogOpen(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${BORDER}`,
          boxShadow: "0 18px 55px rgba(15,23,42,.22)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ ...headerSx, px: 3, py: 2 }}>
          <Typography sx={{ fontWeight: 900 }}>
            {stopMode === "add" ? "Add Bus Stop" : "Edit Bus Stop"}
          </Typography>
          <Typography sx={{ fontSize: ".82rem", opacity: .9 }}>
            Maintain stop name, distance, fee and active status.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: BG, p: { xs: 2, sm: 3 } }}>
        <Paper elevation={0} sx={{ ...popupSectionSx, mt: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(180px,1fr))" },
              gap: 2,
            }}
          >
            <TextField label="Campus" value={campusCode} disabled size="small" sx={inputSx} />
            <TextField
              label="Stop Name"
              value={stopDraft.StopName || ""}
              onChange={(e) => setStopDraft((p) => ({ ...p, StopName: e.target.value }))}
              size="small"
              sx={inputSx}
            />
            <TextField
              label="Distance (KM)"
              type="number"
              value={stopDraft.Distance ?? ""}
              onChange={(e) => setStopDraft((p) => ({ ...p, Distance: e.target.value }))}
              size="small"
              sx={inputSx}
            />
            <TextField
              label="Transport Fee"
              type="number"
              value={stopDraft.TransportFee ?? ""}
              onChange={(e) => setStopDraft((p) => ({ ...p, TransportFee: e.target.value }))}
              size="small"
              sx={inputSx}
            />
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={Number(stopDraft.IsActive) === 1}
                onChange={(e) =>
                  setStopDraft((p) => ({ ...p, IsActive: e.target.checked ? 1 : 0 }))
                }
                sx={checkboxSx}
              />
              <Typography variant="body2">Active</Typography>
            </Stack>
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${BORDER}` }}>
        <Button onClick={() => setStopDialogOpen(false)}>Cancel</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={saveStop} sx={primaryBtnSx}>
          {stopMode === "add" ? "Add Bus Stop" : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const getBusRouteSummary = (bus) => {
    if (!bus) return "";
    const existing = mappings
      .filter((m) => String(m.BusID) === String(bus.BusID))
      .sort((a, b) => Number(a.StopOrder) - Number(b.StopOrder));

    const start = existing[0]?.StopName || "No start";
    const end = existing[existing.length - 1]?.StopName || "No end";
    const driver = bus.DriverName ? ` — ${bus.DriverName}` : "";

    return `${bus.BusNumber}${driver} — ${start} → ${end}`;
  };

  const routeDialog = (
    <Dialog
      open={routeDialogOpen}
      onClose={() => !routeSaving && setRouteDialogOpen(false)}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${BORDER}`,
          boxShadow: "0 18px 55px rgba(15,23,42,.22)",
          minHeight: "72vh",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ ...headerSx, px: 3, py: 2 }}>
          <Typography sx={{ fontWeight: 900 }}>
            {routeMode === "add" ? "Add Bus Route" : "Edit Bus Route"}
          </Typography>
          <Typography sx={{ fontSize: ".82rem", opacity: .9 }}>
            Select a bus, view its current route and insert stops anywhere in the sequence.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: BG, p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(480px, 1.15fr) minmax(360px, .85fr)" },
            gap: 2,
            alignItems: "start",
          }}
        >
          {/* LEFT: route editor */}
          <Stack spacing={2}>
            <Paper elevation={0} sx={popupSectionSx}>
              <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1.5 }}>
                Route Builder
              </Typography>

              <Autocomplete
                options={activeBuses}
                getOptionLabel={(o) => getBusRouteSummary(o)}
                value={
                  activeBuses.find((b) => String(b.BusID) === String(routeBusId)) || null
                }
                renderOption={(props, bus) => {
                  const existing = mappings
                    .filter((m) => String(m.BusID) === String(bus.BusID))
                    .sort((a, b) => Number(a.StopOrder) - Number(b.StopOrder));
                  const start = existing[0]?.StopName || "No start";
                  const end = existing[existing.length - 1]?.StopName || "No end";

                  return (
                    <Box component="li" {...props} key={bus.BusID}>
                      <Box sx={{ width: "100%" }}>
                        <Typography sx={{ fontWeight: 900, color: BLUE }}>
                          {bus.BusNumber}
                          {bus.DriverName ? ` — ${bus.DriverName}` : ""}
                        </Typography>
                        <Typography sx={{ fontSize: ".78rem", color: "text.secondary" }}>
                          {start} → {end}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                onChange={(_, bus) => loadSelectedBusRoute(bus?.BusID || "")}
                disabled={routeMode === "edit"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Bus"
                    size="small"
                    sx={inputSx}
                    helperText={
                      routeMode === "edit"
                        ? "Bus cannot be changed while editing this route."
                        : "Bus options show driver plus current start → end points. After selection, the full route appears on the right."
                    }
                  />
                )}
              />
            </Paper>

            {routeBusId && (
              <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}` }}>
                  <Typography sx={{ fontWeight: 900, color: BLUE }}>
                    Edit Stop Sequence
                  </Typography>
                  <Typography sx={{ fontSize: ".82rem", color: "text.secondary" }}>
                    Use the + buttons between rows to insert a stop exactly where you want it.
                  </Typography>
                </Box>

                <Box sx={{ p: 1.5 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    sx={outlinedBtnSx}
                    onClick={() => insertRouteStop(0)}
                  >
                    Add stop at beginning
                  </Button>

                  <Stack spacing={0} sx={{ mt: 1 }}>
                    {routeStops.map((item, index) => {
                      const opts = availableStopsForIndex(index);
                      const current =
                        activeStops.find(
                          (s) => String(s.StopID) === String(item.StopID)
                        ) || null;

                      return (
                        <React.Fragment key={`${item.BusStopMappingID || "new"}-${index}`}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: `1px solid ${BORDER}`,
                              backgroundColor:
                                index % 2 === 0 ? "rgba(25,118,210,.035)" : "#fff",
                            }}
                          >
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "38px 1fr",
                                  md: "38px minmax(220px,1fr) minmax(180px,.55fr) auto",
                                },
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: "rgba(25,118,210,.08)",
                                  color: BLUE,
                                  border: `2px solid ${BORDER}`,
                                  fontWeight: 900,
                                }}
                              >
                                {index + 1}
                              </Box>

                              <Autocomplete
                                options={opts}
                                getOptionLabel={(o) => o.StopName || ""}
                                value={current}
                                onChange={(_, stop) =>
                                  updateRouteStop(index, {
                                    StopID: stop?.StopID || "",
                                    StopName: stop?.StopName || "",
                                  })
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Bus Stop"
                                    size="small"
                                    sx={inputSx}
                                  />
                                )}
                              />

                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                  label="Stop Time"
                                  value={
                                    item.StopTime
                                      ? dayjs(`2000-01-01T${normalizeTime(item.StopTime)}`)
                                      : null
                                  }
                                  onChange={(v) =>
                                    updateRouteStop(index, {
                                      StopTime: v ? v.format("HH:mm") : "",
                                    })
                                  }
                                  slotProps={{
                                    textField: { size: "small", sx: inputSx },
                                  }}
                                />
                              </LocalizationProvider>

                              <Stack direction="row" spacing={0.2} justifyContent="flex-end">
                                <Tooltip title="Move up">
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={iconBtnSx}
                                      disabled={index === 0}
                                      onClick={() => moveRouteStop(index, -1)}
                                    >
                                      <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Move down">
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={iconBtnSx}
                                      disabled={index === routeStops.length - 1}
                                      onClick={() => moveRouteStop(index, 1)}
                                    >
                                      <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Remove from route">
                                  <IconButton
                                    size="small"
                                    sx={dangerIconBtnSx}
                                    onClick={() => removeRouteStop(index)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Box>
                          </Paper>

                          <Box sx={{ display: "flex", justifyContent: "center", py: .45 }}>
                            <Button
                              size="small"
                              startIcon={<AddCircleOutlineIcon />}
                              onClick={() => insertRouteStop(index + 1)}
                              sx={{
                                minWidth: 0,
                                px: 1.5,
                                py: .25,
                                borderRadius: 10,
                                textTransform: "none",
                                fontWeight: 800,
                                color: THEME_ORANGE,
                              }}
                            >
                              Add stop here
                            </Button>
                          </Box>
                        </React.Fragment>
                      );
                    })}
                  </Stack>

                  {!routeStops.length && (
                    <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                      No stops in this route yet. Use “Add stop at beginning”.
                    </Box>
                  )}
                </Box>
              </Paper>
            )}
          </Stack>

          {/* RIGHT: visual route timeline */}
          <Paper elevation={0} sx={popupSectionSx}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <RouteIcon sx={{ color: THEME_ORANGE }} />
              <Box>
                <Typography sx={{ fontWeight: 900, color: BLUE }}>
                  Existing / Current Route
                </Typography>
                <Typography sx={{ fontSize: ".82rem", color: "text.secondary" }}>
                  {selectedBus
                    ? `${selectedBus.BusNumber}${selectedBus.DriverName ? ` — ${selectedBus.DriverName}` : ""}`
                    : "Select a bus to view its route"}
                </Typography>
              </Box>
            </Stack>

            {!routeBusId ? (
              <Box sx={{ py: 8, textAlign: "center", color: "text.secondary" }}>
                <DirectionsBusIcon sx={{ fontSize: 48, opacity: .25, mb: 1 }} />
                <Typography>Select a bus to view its route and timings.</Typography>
              </Box>
            ) : routeStops.length ? (
              <Box sx={{ pl: 1, pt: 1 }}>
                {routeStops.map((item, index) => (
                  <Box
                    key={`timeline-${index}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "32px 1fr",
                      minHeight: 72,
                    }}
                  >
                    <Box sx={{ position: "relative", display: "flex", justifyContent: "center" }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          mt: .7,
                          borderRadius: "50%",
                          bgcolor: index === 0 || index === routeStops.length - 1 ? THEME_ORANGE : "#fff",
                          border: `3px solid ${index === 0 || index === routeStops.length - 1 ? THEME_ORANGE : BLUE}`,
                          zIndex: 2,
                        }}
                      />
                      {index < routeStops.length - 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 21,
                            bottom: -8,
                            width: 3,
                            bgcolor: "rgba(25,118,210,.28)",
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={{ pb: 2 }}>
                      <Typography sx={{ fontWeight: 900, color: "#222" }}>
                        {item.StopName || "Select stop"}
                      </Typography>
                      <Stack direction="row" spacing={.7} alignItems="center">
                        <AccessTimeIcon sx={{ fontSize: 15, color: THEME_ORANGE }} />
                        <Typography sx={{ fontSize: ".82rem", color: "text.secondary" }}>
                          {item.StopTime || "Time not set"}
                        </Typography>
                      </Stack>
                      <Typography sx={{ fontSize: ".72rem", color: "text.secondary", mt: .2 }}>
                        Stop {index + 1}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ py: 7, textAlign: "center", color: "text.secondary" }}>
                This bus does not have a route yet.
              </Box>
            )}
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${BORDER}` }}>
        <Button
          startIcon={<CancelIcon />}
          onClick={() => setRouteDialogOpen(false)}
          disabled={routeSaving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={saveRoute}
          disabled={routeSaving}
          sx={primaryBtnSx}
        >
          {routeSaving ? "Saving..." : "Save Route"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box
          sx={{
            ...headerSx,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Transport Setup
            </Typography>
            <Typography sx={{ opacity: .9, fontSize: ".85rem", mt: .25 }}>
              Maintain buses, bus stops and route stop mappings from one page.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {activeTab.key === "buses" && (
              <>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openAddBus}
                  sx={{
                    ...primaryBtnSx,
                    backgroundColor: "white",
                    color: THEME_ORANGE,
                    "&:hover": { backgroundColor: "rgba(255,255,255,.92)" },
                  }}
                >
                  Add Bus
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadBusSample}
                  sx={outlinedBtnSx}
                >
                  Download Sample
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  onClick={() => busFileRef.current?.click()}
                  sx={primaryBtnSx}
                >
                  Bulk Upload
                </Button>
                <input
                  hidden
                  type="file"
                  accept=".xlsx,.xls"
                  ref={busFileRef}
                  onChange={(e) => {
                    uploadBuses(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </>
            )}

            {activeTab.key === "stops" && (
              <>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openAddStop}
                  sx={{
                    ...primaryBtnSx,
                    backgroundColor: "white",
                    color: THEME_ORANGE,
                    "&:hover": { backgroundColor: "rgba(255,255,255,.92)" },
                  }}
                >
                  Add Bus Stop
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadStopSample}
                  sx={outlinedBtnSx}
                >
                  Download Sample
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  onClick={() => stopFileRef.current?.click()}
                  sx={primaryBtnSx}
                >
                  Bulk Upload
                </Button>
                <input
                  hidden
                  type="file"
                  accept=".xlsx,.xls"
                  ref={stopFileRef}
                  onChange={(e) => {
                    uploadStops(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </>
            )}

            {activeTab.key === "routes" && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAddRoute}
                sx={{
                  ...primaryBtnSx,
                  backgroundColor: "white",
                  color: THEME_ORANGE,
                  "&:hover": { backgroundColor: "rgba(255,255,255,.92)" },
                }}
              >
                Add Bus Route
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ p: 2, bgcolor: BG }}>
          <Paper elevation={0} sx={surfaceSx}>
            <Tabs
              value={tab}
              onChange={(_, value) => {
                setTab(value);
                setBusDialogOpen(false);
                setStopDialogOpen(false);
                setRouteDialogOpen(false);
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={tabsSx}
            >
              {TRANSPORT_TABS.map((item) => (
                <Tab
                  key={item.key}
                  label={item.label}
                  icon={item.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>

            <Box sx={{ p: 2 }}>
              {activeTab.key === "buses"
                ? renderBusTable()
                : activeTab.key === "stops"
                ? renderStopTable()
                : renderRouteTable()}
            </Box>
          </Paper>
        </Box>
      </Paper>

      {loading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(255,255,255,.45)",
            zIndex: 1600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {busDialog}
      {stopDialog}
      {routeDialog}

      <Snackbar
        open={Boolean(msg.text)}
        autoHideDuration={3500}
        onClose={() => setMsg((p) => ({ ...p, text: "" }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={msg.type}
          onClose={() => setMsg((p) => ({ ...p, text: "" }))}
          sx={{ borderRadius: 2 }}
        >
          {msg.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
